import {
  vArray,
  vBoolean,
  vDate,
  vEmail,
  vFallback,
  type vInput,
  vISODate,
  vObject,
  vOptional,
  type vOutput,
  vPipe,
  vString,
  vStringToNumber,
} from '../validation/index.ts'
import type { ShipmentDirection, Workflow } from './common.ts'
import type {
  AddedBy,
  InvoiceLineItem,
  InvoiceParseMethod,
  InvoiceSource,
} from './invoice.ts'
import type { LoadDocumentDTO } from './load-document.ts'
import type { LoadNote } from './load-note.ts'
import type { SelectedQuote } from './quote.ts'

export const auditStatuses = [
  'outstanding',
  'accepted',
  'disputed',
  'archived',
] as const
export type AuditStatus = (typeof auditStatuses)[number]

export interface Audit {
  createdDate: Date
  isVendor: boolean
  accountId: string
  loadId: string
  quoteRequestId: string
  pickupDate: Date
  direction: ShipmentDirection
  assetCarrierName?: string
  providerName?: string
  bolNum?: string
  trackingNumber?: string
  pickupConfirmationNumber?: string
  quote: SelectedQuote
  invoices: LoadDocumentDTO[]
  invoiceNumber?: string
  invoiceAmount?: number
  invoiceDate?: string
  acceptedDate?: Date
  disputedAmount?: number
  payments?: InvoicePaymentDTO[]
  refNums: Array<{ type: string; value: string }>
  workflow: Workflow
  notes?: LoadNote[]
  statuses: AuditStatus[]
  parseMethod?: InvoiceParseMethod
  sentDisputeEmail: boolean
  pickupDateActual?: Date
  deliveryDate?: Date
  accountLocations: string[]
  tags: string[]
  source?: InvoiceSource
  lineItems: InvoiceLineItem[]
}
interface InvoicePaymentDTO {
  amount: number
  remittedDate?: Date
  remitted?: boolean
  createdDate: Date
  addedBy?: AddedBy
}

export const updateAuditSchema = vObject({
  invoiceNumber: vOptional(vString()),
  invoiceDate: vOptional(vPipe(vString(), vISODate())),
})
export type UpdateAuditDto = vOutput<typeof updateAuditSchema>

export const addPaymentSchema = vObject({
  paymentAmount: vStringToNumber(),
  invoiceAmount: vOptional(vStringToNumber()),
  invoiceNumber: vOptional(vString()),
  invoiceDate: vOptional(vDate()),
  message: vOptional(vString()),
})
export type AddPaymentDto = vOutput<typeof addPaymentSchema>
export type AddPaymentInput = vInput<typeof addPaymentSchema>

export const disputeInvoiceSchema = vObject({
  amount: vStringToNumber(),
  emails: vArray(vEmail),
  invoiceNumber: vOptional(vString()),
  invoiceDate: vOptional(vDate()),
  message: vOptional(vString()),
})
export type DisputeInvoiceDto = vOutput<typeof disputeInvoiceSchema>
export type DisputeInvoiceInput = vInput<typeof disputeInvoiceSchema>

export const remitPaymentsSchema = vObject({
  loadIds: vArray(vString()),
  doExport: vOptional(vBoolean()),
  removeDispute: vFallback(vOptional(vBoolean()), false),
})
export type RemitPaymentsDto = vOutput<typeof remitPaymentsSchema>

export interface AuditCountsDTO {
  outstanding: number
  accepted: number
  disputed: number
  archived: number
}
